import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faBuilding,
  faMoneyBillTrendUp,
  faShieldHalved,
  faUser,
  faUserLock,
} from "@fortawesome/pro-regular-svg-icons";
import { uniq } from "lodash-es";

import {
  CaseQueueColumnKey,
  CaseQueueColumnSchemas,
  DeserializedCaseQueue,
  DeserializedCaseTemplateVersion,
  DeserializedQueueCase,
  EnrichedDeserializedQueueCase,
} from "src/cases/queues/types";
import { CasePropertiesValue } from "src/clients/case-management";
import {
  JsonApiResourceObjectUnionQueueMemberAttributesQueueTemplateConfigAttributesAny,
  QueueAtomicOperationsRequest,
  QueueCreateAttributes,
} from "src/clients/case-management-service";

export const CASE_QUEUE_ICONS: Record<string, IconProp> = {
  "shield-halved": faShieldHalved,
  "user-lock": faUserLock,
  buildings: faBuilding,
  user: faUser,
  "money-bill-trend-up": faMoneyBillTrendUp,
} as const;

export const getCaseQueueIcon = (icon?: string | null) => {
  if (!icon || !(icon in CASE_QUEUE_ICONS)) return null;
  return CASE_QUEUE_ICONS[icon];
};

export const extractInitialFormValues = (queue?: DeserializedCaseQueue) => {
  if (!queue) return;

  return {
    attributes: {
      name: queue.name,
      icon: queue.icon,
    },
    memberIds: queue.members.map(({ user }) => user.id),
    templateIds: queue.template_configs.map(({ template }) => template.id),
  };
};

type CreateQueueAtomicOperationParams = {
  queueId: string;
  attributes: QueueCreateAttributes;
  templateIds: string[];
  memberIds: string[];
};

type QueueAtomicOperationInclude =
  JsonApiResourceObjectUnionQueueMemberAttributesQueueTemplateConfigAttributesAny;

export const createQueueAtomicOperationIncludes = ({
  memberIds,
  templateIds,
}: Omit<
  CreateQueueAtomicOperationParams,
  "queueId" | "attributes"
>): QueueAtomicOperationsRequest["included"] => {
  return [
    ...memberIds.map(
      (id) =>
        ({
          type: "queue_members" as const,
          id,
          attributes: { type: "user" },
          relationships: {
            user: { data: { type: "users", id } },
          },
        }) as unknown as QueueAtomicOperationInclude,
    ),
    ...templateIds.map(
      (id) =>
        ({
          type: "template_configs" as const,
          id,
          attributes: { assignment_logic: "self-assign" },
          relationships: {
            template: {
              data: { type: "templates", id },
            },
          },
        }) as unknown as QueueAtomicOperationInclude,
    ),
  ];
};

export const createQueueAtomicOperation = ({
  queueId,
  memberIds,
  attributes,
  templateIds,
}: CreateQueueAtomicOperationParams): QueueAtomicOperationsRequest["atomic:operations"] => {
  return [
    {
      op: "update",
      ref: {
        type: "queues",
        id: queueId,
        relationship: "attributes",
      },
      data: attributes,
    },
    {
      op: "update",
      ref: {
        type: "queues",
        id: queueId,
        relationship: "template_configs",
      },
      data: templateIds.map((id) => ({
        type: "template_configs",
        id,
      })),
    },
    {
      op: "update",
      ref: {
        type: "queues",
        id: queueId,
        relationship: "members",
      },
      data: memberIds.map((id) => ({
        type: "queue_members",
        id,
      })),
    },
  ];
};

export const RESERVED_PROPERTY_KEYS = ["status", "sub_status"];

/**
 *  Extracts a unique set of column schemas from an array of case template versions.
 *
 * This utility processes multiple case template version objects and aggregates
 * all unique case property schemas from their `case_properties` fields into a flat object.
 * A unique schema key (generated by `createColumnKey`) ensures that each schema
 * is only included once, even if it appears in multiple versions.
 *
 */
export const extractQueueColumnSchemas = (
  versions: DeserializedCaseTemplateVersion[],
) => {
  return versions.reduce((columnSchemas, version) => {
    const propertyEntries = Object.entries(version.case_properties);

    propertyEntries.forEach(([propertyKey, schema]) => {
      const propertySchema = schema as CasePropertiesValue;

      const schemaKey = createColumnKey(propertyKey, propertySchema);
      const columnSchema = columnSchemas[schemaKey];

      const withOption = propertySchema.type === "enum";

      if (columnSchema) {
        // if the schema is enum or tags, we need to merge options
        if (withOption && propertySchema.type === columnSchema.type) {
          const propertyValues = propertySchema.values ?? [];
          const propertyOptions = propertyValues.map((v) => v.value);

          // merge unique options
          columnSchema.options = uniq([
            ...columnSchema.options,
            ...propertyOptions,
          ]);
        }
      } else {
        if (withOption) {
          // create new schema with options
          columnSchemas[schemaKey] = {
            type: propertySchema.type,
            display_name: propertySchema.display_name,
            options: propertySchema.values?.map((v) => v.value) ?? [],
            __key: propertyKey,
          };
        } else {
          // create new schema without options
          columnSchemas[schemaKey] = {
            type: propertySchema.type,
            display_name: propertySchema.display_name,
            __key: propertyKey,
          };
        }
      }
    });

    return columnSchemas;
  }, {} as CaseQueueColumnSchemas);
};

const createColumnKey = (
  key: string,
  schema: CasePropertiesValue,
): CaseQueueColumnKey => {
  let schemaKey = `${schema.type}.${key}`;

  if (!RESERVED_PROPERTY_KEYS.includes(key)) {
    const displayName = schema.display_name.replaceAll(" ", "_");
    schemaKey += `.${displayName}`;
  }

  return schemaKey as CaseQueueColumnKey;
};

/**
 * Enriches a case object by mapping its custom properties onto
 * standardized column keys based on provided column schemas
 */
export const enrichCaseWithColumnValues = (
  caseData: DeserializedQueueCase,
  queueColumnSchemas: CaseQueueColumnSchemas,
) => {
  const enrichedCaseData = {
    ...caseData,
  } as EnrichedDeserializedQueueCase;

  const customProperties = caseData.case_properties;
  const columnKeys = Object.keys(queueColumnSchemas).filter(
    (key): key is CaseQueueColumnKey => !!key,
  );

  for (const columnKey of columnKeys) {
    const columnSchema = queueColumnSchemas[columnKey];
    enrichedCaseData[columnKey] = columnSchema
      ? customProperties[columnSchema.__key]
      : undefined;
  }

  return enrichedCaseData;
};
